import { ApiIncrementalityTest } from "@incendium/api";
import { Add, Edit } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Button,
  IconButton,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import SpacedList, { SpacedLinkButton } from "Components/UI/SpacedList";
import { cell3Icons } from "consts";
import { friendlyDate } from "Helpers/dates";
import { useConfirmation } from "Hooks/useConfirmation";
import { useCallback } from "react";
import {
  CONTROL_PERIOD_LABEL,
  DELETE_TEST_CONFIRM_BODY,
  DELETE_TEST_CONFIRM_TITLE,
  TEST_STATUS_LABELS,
} from "../helpers/copy";

type IncrementalityTestsListProps = {
  tests: ApiIncrementalityTest[];
  onOpen: (id: number) => void;
  onCreate: () => void;
  onDelete: (test: ApiIncrementalityTest) => Promise<string>;
};

function IncrementalityTestsList({
  tests,
  onOpen,
  onCreate,
  onDelete,
}: IncrementalityTestsListProps) {
  const handleDelete = useConfirmation();
  const handleDeleteClick = useCallback(
    (test: ApiIncrementalityTest) => {
      handleDelete({
        title: DELETE_TEST_CONFIRM_TITLE,
        body: DELETE_TEST_CONFIRM_BODY,
        callback: async () => onDelete(test),
      });
    },
    [handleDelete, onDelete],
  );

  return (
    <SpacedList
      title="Incrementality Tests"
      isEmpty={tests.length === 0}
      action={
        <Button startIcon={<Add />} onClick={onCreate}>
          New Test
        </Button>
      }
    >
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
          <TableCell>Test Region</TableCell>
          <TableCell>{CONTROL_PERIOD_LABEL}</TableCell>
          <TableCell>Test Dates</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Created On</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {tests.map((test) => (
          <TableRow key={test.id}>
            <TableCell className="primary">
              <SpacedLinkButton onClick={() => test.id && onOpen(test.id)}>
                {test.name || `Test #${test.id}`}
              </SpacedLinkButton>
            </TableCell>
            <TableCell>
              {[test.testRegionCity, test.testRegionCountry]
                .filter(Boolean)
                .join(", ") || "-"}
            </TableCell>
            <TableCell>
              {test.preTestFrom && test.preTestTo
                ? `${friendlyDate(test.preTestFrom)} - ${friendlyDate(test.preTestTo)}`
                : "-"}
            </TableCell>
            <TableCell>
              {test.testFrom && test.testTo
                ? `${friendlyDate(test.testFrom)} - ${friendlyDate(test.testTo)}`
                : "-"}
            </TableCell>
            <TableCell>
              {TEST_STATUS_LABELS[test.status || 0] || "Unspecified"}
            </TableCell>
            <TableCell>
              {test.createdAt ? friendlyDate(test.createdAt) : "-"}
            </TableCell>
            <TableCell align="right" style={{ width: cell3Icons }}>
              <IconButton
                size="small"
                color="primary"
                onClick={() => test.id && onOpen(test.id)}
              >
                <Edit />
              </IconButton>
              <IconButton
                size="small"
                color="secondary"
                onClick={() => handleDeleteClick(test)}
              >
                <DeleteIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </SpacedList>
  );
}

export default IncrementalityTestsList;
